/**
 * A player whose sound is Nova's playout (apps/playout, phase E): the same methods and
 * snapshot as the window's own player (player.ts), so every screen that drives On air
 * keeps working, but each method is a command to the playout and the snapshot is what
 * the playout last said. Nothing sounds in the window.
 *
 * - The queue is the playout's (its `Queue` event); items keep the keys this window
 *   gave them, and the playout's own (Auto DJ, fixed times) start with "p".
 * - The position is carried forward between states on the state's own clock (`atMs`).
 * - Volume, crossfade and levelling are remembered here (the window's preferences, as
 *   before) and given to the playout every time it connects, so a restarted playout
 *   comes back with the station's settings.
 * - A playout that comes back empty after it was playing (it restarted) is handed the
 *   queue it had and carries on from where it was.
 * - Sound inside the window (`extraInput`, `liveInput`, `analyser`) is null here: carts,
 *   the mic, presenters and the meter go to the playout their own way.
 */

import { create } from "@bufbuild/protobuf";
import { bus } from "./bus";
import { readPref, writePref } from "./prefs";
import { ArrangeEntrySchema, type Item, type Track } from "./gen/nova/playout/v1/playout_pb";
import { toPlayoutItem, toPlayoutTrack, type PlayoutClient, type PlayoutCommand } from "./playout";
import type { Overlay, PlayerSnapshot, QueueItem, QueueTrack } from "./player";
import type { Track as LibraryTrack } from "../types";
import { toQueueTrack } from "./player";

let keySeq = 0;
const KEY_PREFIX = Math.random().toString(36).slice(2, 7);
const nextKey = () => `w${KEY_PREFIX}${++keySeq}`;

/** A contract track as a window queue track. */
export function fromPlayoutTrack(t: Track | undefined): QueueTrack {
  return {
    id: t?.id ?? "",
    title: t?.title ?? "",
    artist: t?.artist ?? "",
    album: t?.album || null,
    year: t?.year ?? null,
    bpm: t?.bpm ?? null,
    musicalKey: t?.musicalKey || null,
    lufs: t?.lufs ?? null,
    peak: t?.peak ?? null,
    cueIn: t?.cueIn ?? null,
    cueOut: t?.cueOut ?? null,
    mixAt: t?.mixAt ?? null,
    introEnd: t?.introEnd ?? null,
    duration: t?.duration ?? 0,
  };
}

/** A contract item as a window queue item. */
export function fromPlayoutItem(i: Item): QueueItem {
  return {
    key: i.key,
    track: fromPlayoutTrack(i.track),
    pinned: i.pinned || undefined,
    auto: i.auto || undefined,
    reason: i.reason || undefined,
    fadeOut: i.fadeOut,
    trimmed: i.trimmed,
    overlays: i.overlays.length
      ? i.overlays.map((o): Overlay => ({ track: fromPlayoutTrack(o.track), at: o.at, reason: o.reason || undefined, placement: o.placement || undefined }))
      : undefined,
    from: i.from || undefined,
    hold: i.hold || undefined,
  };
}

const toTrack = (t: LibraryTrack | QueueTrack): QueueTrack => ("path" in t ? toQueueTrack(t) : t);
const newItem = (t: LibraryTrack | QueueTrack): QueueItem => ({ key: nextKey(), track: toTrack(t) });

export interface PlayoutPlayerOptions {
  /** Prefix of its bus events ("onair"): `<events>.track`, `.state`, `.skipped`, `.trimmed`. */
  events: string;
  /** Prefix of the remembered settings ("nova.onair"): volume, crossfade, levelling. */
  prefs: string;
  /** Where the chosen sound card's name is remembered: given to the playout on connect. */
  outputPref?: string;
}

export class PlayoutPlayer {
  private listeners = new Set<() => void>();
  private queue: QueueItem[] = [];
  private queueVersion = -1n;
  private unplayable = new Set<string>();
  private muted = false;
  private snap: PlayerSnapshot;
  /** What was playing when last heard, to carry on after the playout restarts. */
  private last: { queue: QueueItem[]; index: number; position: number; playing: boolean } | null = null;
  private wasConnected = false;
  private offs: (() => void)[] = [];

  constructor(private readonly c: PlayoutClient, private readonly o: PlayoutPlayerOptions) {
    const pref = <T,>(k: string, d: T) => readPref<T>(`${o.prefs}.${k}`, d);
    this.snap = {
      queue: [], source: null, index: -1, current: null, playing: false, loading: false, position: 0, duration: 0,
      volume: pref("volume", 0.8), muted: false, shuffle: false, repeat: "off", crossfade: pref("crossfade", 6),
      levelling: pref("levelling", true), stopAfter: false, automation: false, ducked: false, error: null,
    };
    this.offs.push(c.subscribe(() => this.heard()));
    // Songs passed over and played items let go, on the bus as the window player's were.
    this.offs.push(c.onEvent((ev) => {
      if (ev.case === "skipped") bus.emit(`${o.events}.skipped`, { trackId: ev.value.trackId, title: ev.value.title, why: ev.value.why });
      if (ev.case === "trimmed") bus.emit(`${o.events}.trimmed`, { keys: ev.value.keys });
    }));
    this.heard();
  }

  /** Stop following the playout (the window gave On air up, or went back to its own player). */
  dispose() {
    this.offs.forEach((off) => off());
    this.offs = [];
  }

  get id() {
    return "onair";
  }
  get events() {
    return this.o.events;
  }

  subscribe = (fn: () => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };
  get = () => this.snap;

  private send(cmd: PlayoutCommand) {
    void this.c.send(cmd);
  }

  /** The playout spoke: rebuild the snapshot. */
  private heard() {
    const connected = this.c.connected();
    if (connected && !this.wasConnected) this.connectedNow();
    this.wasConnected = connected;
    const q = this.c.queue();
    if (q && q.version !== this.queueVersion) {
      this.queueVersion = q.version;
      this.queue = q.items.map(fromPlayoutItem);
    }
    const s = this.c.state();
    const before = this.snap;
    if (!s) return;
    const moved = s.playing ? Math.max(0, (Date.now() - Number(s.atMs)) / 1000) : 0;
    const index = s.index < this.queue.length ? s.index : -1;
    const current = this.queue[index] ?? null;
    this.snap = {
      ...before,
      queue: this.queue,
      index,
      current,
      playing: s.playing,
      loading: s.loading,
      position: Math.min(s.position + moved, s.duration || Infinity),
      duration: s.duration,
      volume: this.muted ? before.volume : s.volume,
      muted: this.muted,
      crossfade: s.crossfade,
      levelling: s.levelling,
      automation: s.automation,
      ducked: s.ducked,
      error: s.error || (connected ? null : "Nova's playout is not answering."),
    };
    if (this.queue.length) this.last = { queue: this.queue, index, position: this.snap.position, playing: s.playing };
    this.listeners.forEach((fn) => fn());
    if (before.current?.key !== current?.key) bus.emit(`${this.o.events}.track`, { trackId: current?.track.id ?? null });
    if (before.playing !== s.playing) bus.emit(`${this.o.events}.state`, { playing: s.playing });
  }

  /**
   * Connected (or connected again): what it was playing, if it lost it. The station's
   * settings are the playout's own now (it reads and saves them in the engine), so they
   * are not pushed from here; `handOver` gives a window's old ones the first time.
   */
  private connectedNow() {
    const last = this.last;
    // Each desk waits a different while, so the first to look hands it back and the
    // others find it playing (every window is a desk of the playout).
    setTimeout(() => {
      // It came back empty after playing: it restarted. Hand it back what it had.
      if (last && !this.c.queue()?.items.length) void this.restore(last, 1);
    }, 500 + Math.random() * 1500);
  }

  /** This window's own settings, given to the playout once (the engine had none yet). */
  handOver(extra: { output?: string } = {}) {
    // This computer's own, as remembered (the snapshot already shows the playout's).
    const pref = <T,>(k: string, d: T) => readPref<T>(`${this.o.prefs}.${k}`, d);
    this.send({ case: "setVolume", value: { volume: pref("volume", 0.8) } });
    this.send({ case: "setCrossfade", value: { seconds: pref("crossfade", 6) } });
    this.send({ case: "setLevelling", value: { on: pref("levelling", true) } });
    const card = extra.output ?? (this.o.outputPref ? readPref<string>(this.o.outputPref, "") : "");
    if (card) this.send({ case: "setOutput", value: { id: card } });
  }

  // ---------------------------------------------------------------- transport

  play() {
    this.send({ case: "play", value: {} });
  }
  pause() {
    this.send({ case: "pause", value: {} });
  }
  toggle() {
    if (this.snap.playing) this.pause();
    else this.play();
  }
  next({ fade = false, seconds }: { fade?: boolean; seconds?: number } = {}) {
    this.send({ case: "next", value: { fade, seconds } });
  }
  prev() {
    if (this.snap.index > 0) void this.jump(this.snap.index - 1);
  }
  async jump(i: number, { fade = false, seconds, at }: { fade?: boolean; seconds?: number; at?: number } = {}) {
    await this.c.send({ case: "jump", value: { index: i, at, fade, seconds } });
  }
  seek(seconds: number) {
    this.send({ case: "seek", value: { seconds } });
  }
  stop(seconds = 1) {
    this.send({ case: "stop", value: { seconds } });
  }
  /** Nothing to wake: the sound is the playout's. */
  async wake() {}

  // ---------------------------------------------------------------- settings

  setVolume(v: number) {
    const volume = Math.max(0, Math.min(1, v));
    writePref(`${this.o.prefs}.volume`, volume);
    this.muted = false;
    this.snap = { ...this.snap, volume, muted: false };
    this.send({ case: "setVolume", value: { volume } });
  }
  toggleMute() {
    this.muted = !this.muted;
    this.snap = { ...this.snap, muted: this.muted };
    this.send({ case: "setVolume", value: { volume: this.muted ? 0 : this.snap.volume } });
    this.listeners.forEach((fn) => fn());
  }
  setCrossfade(seconds: number) {
    const crossfade = Math.max(0, Math.min(12, seconds));
    writePref(`${this.o.prefs}.crossfade`, crossfade);
    this.send({ case: "setCrossfade", value: { seconds: crossfade } });
  }
  setLevelling(on: boolean) {
    writePref(`${this.o.prefs}.levelling`, on);
    this.send({ case: "setLevelling", value: { on } });
  }
  setAutomation(on: boolean) {
    this.send({ case: "setAutomation", value: { on } });
  }
  setShuffle() {}
  cycleRepeat() {}
  setStopAfter() {}

  // ---------------------------------------------------------------- the queue

  private insert(items: QueueItem[], index: number | undefined, startIfIdle: boolean) {
    if (!items.length) return;
    this.send({ case: "insert", value: { index, items: items.map(toPlayoutItem), startIfIdle } });
  }
  playTracks(tracks: (LibraryTrack | QueueTrack)[], start = 0) {
    const items = tracks.map(newItem);
    this.send({ case: "setQueue", value: { items: items.map(toPlayoutItem) } });
    void this.jump(Math.max(0, Math.min(start, items.length - 1)));
  }
  /** A flow's queue is never On air's. */
  replaceUpcoming(): boolean {
    return false;
  }
  extend(): boolean {
    return false;
  }
  enqueue(tracks: (LibraryTrack | QueueTrack)[]) {
    this.insert(tracks.map(newItem), this.queue.length, true);
  }
  playNext(tracks: (LibraryTrack | QueueTrack)[]) {
    this.insert(tracks.map(newItem), undefined, true);
  }
  appendPlanned(entries: { track: QueueTrack; reason?: string; trimTo?: number; fadeOut?: number; overlays?: Overlay[]; from?: string }[],
    { auto = false }: { auto?: boolean } = {}) {
    const items = entries.map((e): QueueItem => {
      const trimmed = e.trimTo !== undefined && e.track.cueOut !== null ? e.track.cueOut - e.trimTo
        : e.trimTo !== undefined ? e.track.duration - e.trimTo : 0;
      const track = e.trimTo !== undefined ? { ...e.track, cueOut: e.trimTo } : e.track;
      return { key: nextKey(), track, reason: e.reason, fadeOut: e.fadeOut, trimmed: trimmed > 0 ? trimmed : undefined, overlays: e.overlays, from: e.from, auto: auto || undefined };
    });
    this.insert(items, this.queue.length, true);
  }
  insertNext(entries: { track: QueueTrack; reason?: string }[]) {
    this.insert(entries.map((e) => ({ key: nextKey(), track: e.track, reason: e.reason })), undefined, false);
  }
  arrangeUpcoming(order: (string | { track: QueueTrack; reason?: string })[]) {
    const entries = order.map((o) => create(ArrangeEntrySchema, typeof o === "string"
      ? { entry: { case: "key", value: o } }
      : { entry: { case: "item", value: toPlayoutItem({ key: nextKey(), track: o.track, reason: o.reason }) } }));
    this.send({ case: "arrange", value: { order: entries } });
  }
  removeKeys(keys: Set<string>) {
    if (keys.size) this.send({ case: "remove", value: { keys: [...keys] } });
  }
  removeAt(i: number) {
    const item = this.queue[i];
    if (item && i !== this.snap.index) this.send({ case: "remove", value: { keys: [item.key] } });
  }
  move(from: number, to: number) {
    this.send({ case: "move", value: { from, to } });
  }
  setPinned(i: number, on: boolean) {
    const item = this.queue[i];
    if (item) this.send({ case: "setFlags", value: { key: item.key, pinned: on } });
  }
  setHold(i: number, on: boolean) {
    const item = this.queue[i];
    if (item) this.send({ case: "setFlags", value: { key: item.key, hold: on } });
  }
  addOverlay(track: QueueTrack, at: number, reason?: string): boolean {
    if (!this.snap.current) return false;
    this.send({ case: "addOverlay", value: { track: toPlayoutTrack(track), at, reason: reason ?? "" } });
    return true;
  }
  clear() {
    this.send({ case: "clear", value: {} });
  }

  /**
   * Carry on a queue from elsewhere (another window, or the playout restarted). The
   * playout keeps its queue whatever window drives it: only an empty one is given this.
   */
  async restore(snap: { queue: QueueItem[]; index: number; position: number; playing: boolean }, fadeIn = 0.6) {
    if (this.c.queue()?.items.length || !snap.queue.length) return;
    await this.c.send({ case: "setQueue", value: { items: snap.queue.map(toPlayoutItem) } });
    if (snap.index < 0 || snap.index >= snap.queue.length) return;
    if (snap.playing) await this.jump(snap.index, { fade: fadeIn > 0, seconds: fadeIn, at: snap.position });
  }

  // ---------------------------------------------------------------- the rest of On air

  setUnplayable(ids: Iterable<string>, why: string) {
    this.unplayable = new Set(ids);
    this.send({ case: "setUnplayable", value: { trackIds: [...this.unplayable], why } });
  }
  isUnplayable(trackId: string): boolean {
    return this.unplayable.has(trackId);
  }
  dip(source: string, db: number | null, seconds = 0.4) {
    this.send({ case: "dip", value: { source, db: db === null || db <= 0 ? undefined : db, seconds } });
  }
  duck(on: boolean) {
    this.send({ case: "duck", value: { on } });
  }
  /** Sound in the window: none (carts, the mic and presenters are the playout's). */
  async extraInput(): Promise<null> {
    return null;
  }
  async liveInput(): Promise<null> {
    return null;
  }
  analyser(): null {
    return null;
  }
}
